import z from "zod";
import {
  PaymentLinkIdScheme,
  PaymentMethodIdScheme,
  ReceiptIdScheme,
  SaleIdScheme,
  StaffIdScheme,
} from "./ids";
import { CartItemDBScheme } from "./cartItem";
import { DiscountScheme } from "./discount";
import { MoneyScheme } from "./money";
import { PaymentLinkStatus } from "./enums/PaymentLinkStatus";
import { CustomerInfoDBScheme } from "./customerInfo";
import {
  StoreFrontPaymentMethodCreditNoteScheme,
  StoreFrontPaymentMethodGiftCardScheme,
  StoreFrontPaymentMethodSwishCommerceScheme,
  StoreFrontPaymentMethodVivaSmartCheckoutScheme,
} from "./storeFront/storeFront";
import type { OnlineOrderSettings } from "./onlineOrderSettings";
import type { RestSale } from "./sale";
import type { RestGetReceiptPayment } from "./receiptPayment";
import type { Currency } from "./enums/currency";
import type { Country } from "./enums/Country";

export const PAYMENT_LINK_DEFAULT_VALIDITY_DAYS = 7;
export const PAYMENT_LINK_MAX_VALIDITY_DAYS = 90;

export const PaymentLinkPaymentMethodScheme = z.discriminatedUnion("type", [
  StoreFrontPaymentMethodSwishCommerceScheme,
  StoreFrontPaymentMethodVivaSmartCheckoutScheme,
  StoreFrontPaymentMethodGiftCardScheme,
  StoreFrontPaymentMethodCreditNoteScheme,
]);

export const PaymentLinkDBScheme = z.object({
  id: PaymentLinkIdScheme,
  paymentMethodId: PaymentMethodIdScheme,
  status: z.enum(PaymentLinkStatus),
  items: z.array(CartItemDBScheme),
  discount: DiscountScheme.nullable(),
  total: MoneyScheme,
  message: z.string().nullable(),
  customer: CustomerInfoDBScheme.optional(),
  staffId: StaffIdScheme.nullable(),
  saleId: SaleIdScheme.nullable(),
  receiptId: ReceiptIdScheme.nullable(),
  expiresAt: z.iso.datetime(),
  paidAt: z.iso.datetime().nullable(),
  fiscalize: z.boolean().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const RestGetPaymentLink = PaymentLinkDBScheme.extend({
  url: z.string(),
});

export const RestCreatePaymentLink = z.object({
  paymentMethodId: PaymentMethodIdScheme,
  items: z.array(CartItemDBScheme).min(1),
  discount: DiscountScheme.nullable(),
  message: z.string().max(500).nullable().optional(),
  customer: CustomerInfoDBScheme.optional(),
  staffId: StaffIdScheme.nullable().optional(),
});

export const RestSendPaymentLinkEmail = z.object({
  email: z.email(),
});

export const RestStartPaymentLinkPayment = z.object({
  paymentMethodId: PaymentMethodIdScheme,
  cardNumber: z.string().trim().min(1).max(64).optional(),
  barcode: z.string().trim().min(1).max(64).optional(),
});

export type PaymentLinkPaymentMethod = z.infer<
  typeof PaymentLinkPaymentMethodScheme
>;
export type DBPaymentLink = z.infer<typeof PaymentLinkDBScheme>;
export type RestPaymentLink = z.infer<typeof RestGetPaymentLink>;
export type RestCreatePaymentLink = z.infer<typeof RestCreatePaymentLink>;
export type RestSendPaymentLinkEmail = z.infer<typeof RestSendPaymentLinkEmail>;
export type RestStartPaymentLinkPayment = z.infer<
  typeof RestStartPaymentLinkPayment
>;

/** The parts of a payment link's sale the public pay page may see: no customer, staff or card details. */
export type PublicPaymentLinkSale = Pick<
  RestSale,
  "id" | "status" | "pendingPaymentData" | "errorMessage" | "rounding" | "tip"
> & {
  payments: Pick<
    RestGetReceiptPayment,
    "paymentMethodId" | "type" | "name" | "amount" | "paidAt"
  >[];
};

export type PublicPaymentLink = {
  paymentLink: Omit<
    DBPaymentLink,
    "staffId" | "customer" | "fiscalize" | "paymentMethodId"
  >;
  paymentMethods: {
    id: PaymentLinkPaymentMethod["id"];
    name: string;
    type: PaymentLinkPaymentMethod["type"];
  }[];
  store: {
    name: string;
    companyName: string;
    orgNumber: string;
    street: string;
    postalcode: string;
    city: string;
    country: Country;
  };
  appearance: OnlineOrderSettings | null;
  currency: Currency;
  sale: PublicPaymentLinkSale | null;
};
